import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';

import { appendYowanPreviewQueryIfNeeded } from '../../../app/host/yowanPreviewRouting';
import { LoadingSpinner } from '../../../shared/ui';
import {
  fetchYowanRelatedProfile,
  getYowanAdminMutationErrorMessage,
  updateYowanRelatedInstructorProfile,
  updateYowanRelatedInstructorProfileImage,
  updateYowanRelatedOrganizerProfile,
  updateYowanRelatedOrganizerProfileImage,
  validateYowanImageUploadFile,
  YOWAN_IMAGE_UPLOAD_HELP_TEXT,
} from '../app/yowanContentClient';
import {
  AdminCurrentImagePreview,
  AdminPageHeader,
} from './AdminBlogListPage';
import {
  StakeholderGuardNotice,
  StakeholderShell,
  useStakeholderGuard,
} from './StakeholderSeminarsPage';
const { yowanPageContent } = require('./pageContent');

const FieldNote = ({ children }) => {
  if (!children) {
    return null;
  }

  return <p className="yowan-admin-form__field-note">{children}</p>;
};

const getUploadFile = (value) => {
  if (!value) {
    return null;
  }

  if (typeof File !== 'undefined' && value instanceof File) {
    return value;
  }

  if (Array.isArray(value)) {
    return value[0] || null;
  }

  if (value.length !== undefined) {
    return value[0] || null;
  }

  return null;
};

const ProfileStatusFields = ({ record }) => (
  <>
    <div className="c-form__field">
      <span className="c-form__label">稼働状態</span>
      <input className="c-form__control" type="text" value={record.isActive ? '稼働中' : '停止中'} readOnly disabled />
      <FieldNote>変更が必要な場合は管理者へご相談ください。</FieldNote>
    </div>
    <div className="c-form__field">
      <span className="c-form__label">公開プロフィール</span>
      <input className="c-form__control" type="text" value={record.publicProfileEnabled ? '公開' : '非公開'} readOnly disabled />
      <FieldNote>変更が必要な場合は管理者へご相談ください。</FieldNote>
    </div>
  </>
);

const getStakeholderProfileRouteKey = (pathname = '') => {
  if (pathname.startsWith('/stakeholder/instructor-profile')) {
    return '/stakeholder/instructor-profile';
  }

  if (pathname.startsWith('/stakeholder/organizer-profile')) {
    return '/stakeholder/organizer-profile';
  }

  return '/stakeholder/profile';
};

const InstructorProfileForm = ({ record, onUpdated, onSaved, contentKey = '/stakeholder/profile' }) => {
  const content = yowanPageContent[contentKey] || yowanPageContent['/stakeholder/profile'];
  const [submitMessage, setSubmitMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({ defaultValues: { ...record, image: '' } });

  useEffect(() => {
    reset({ ...record, image: '' });
  }, [record, reset]);

  const onSubmit = async (values) => {
    setIsSubmitting(true);
    setSubmitMessage(content.form.pendingMessage);

    try {
      let updated = await updateYowanRelatedInstructorProfile(record.id, values);
      const imageFile = getUploadFile(values.image);

      if (imageFile) {
        const imageUpdate = await updateYowanRelatedInstructorProfileImage(record.id, imageFile);
        updated = imageUpdate?.id ? imageUpdate : { ...updated, ...imageUpdate };
      }

      onUpdated(updated);
      reset({ ...updated, image: '' });
      setSubmitMessage(content.form.updatedMessage);
      onSaved?.();
    } catch (error) {
      setSubmitMessage(getYowanAdminMutationErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form className="yowan-admin-form c-surface-content" onSubmit={handleSubmit(onSubmit)}>
      <header className="yowan-stakeholder-profile-card__header">
        <h2>講師プロフィール</h2>
        <p>公開プロフィールとして表示される講師情報を更新できます。</p>
      </header>
      <div className="c-form__field">
        <span className="c-form__label">講師名<em className="c-form__required">*</em></span>
        <input className="c-form__control" type="text" {...register('instructorName', { required: 'この項目は必須です。' })} />
        {errors.instructorName ? <em className="yowan-admin-form__error">{errors.instructorName.message}</em> : null}
      </div>
      <div className="c-form__field">
        <span className="c-form__label">メールアドレス<em className="c-form__required">*</em></span>
        <input className="c-form__control" type="email" {...register('instructorEmail', { required: 'この項目は必須です。' })} />
        {errors.instructorEmail ? <em className="yowan-admin-form__error">{errors.instructorEmail.message}</em> : null}
      </div>
      <div className="c-form__field">
        <span className="c-form__label">肩書き<em className="c-form__required">*</em></span>
        <input className="c-form__control" type="text" {...register('roleLabel', { required: 'この項目は必須です。' })} />
        {errors.roleLabel ? <em className="yowan-admin-form__error">{errors.roleLabel.message}</em> : null}
      </div>
      <div className="c-form__field">
        <span className="c-form__label">専門領域</span>
        <input className="c-form__control" type="text" {...register('specialties')} />
      </div>
      <div className="c-form__field">
        <span className="c-form__label">紹介文<em className="c-form__required">*</em></span>
        <textarea className="c-form__control" rows="4" maxLength="220" {...register('biography', { required: 'この項目は必須です。' })} />
        {errors.biography ? <em className="yowan-admin-form__error">{errors.biography.message}</em> : null}
        <FieldNote>220文字以内で入力してください。</FieldNote>
      </div>
      <div className="c-form__field">
        <span className="c-form__label">経歴<em className="c-form__required">*</em></span>
        <textarea className="c-form__control" rows="6" maxLength="800" {...register('career', { required: 'この項目は必須です。' })} />
        {errors.career ? <em className="yowan-admin-form__error">{errors.career.message}</em> : null}
        <FieldNote>800文字以内で入力してください。</FieldNote>
      </div>
      <div className="c-form__field">
        <span className="c-form__label">プロフィール画像</span>
        <AdminCurrentImagePreview image={record.primary_image} />
        <input className="c-form__control" type="file" accept="image/*" {...register('image', { validate: validateYowanImageUploadFile })} />
        {errors.image ? <em className="yowan-admin-form__error">{errors.image.message}</em> : null}
        <FieldNote>{YOWAN_IMAGE_UPLOAD_HELP_TEXT}</FieldNote>
      </div>
      <div className="c-form__field">
        <span className="c-form__label">Webサイト</span>
        <input className="c-form__control" type="url" {...register('webLinkWebsite')} />
      </div>
      <div className="c-form__field">
        <span className="c-form__label">Facebook</span>
        <input className="c-form__control" type="url" {...register('webLinkFacebook')} />
      </div>
      <div className="c-form__field">
        <span className="c-form__label">X</span>
        <input className="c-form__control" type="url" {...register('webLinkX')} />
      </div>
      <div className="c-form__field">
        <span className="c-form__label">Instagram</span>
        <input className="c-form__control" type="url" {...register('webLinkInstagram')} />
      </div>
      <ProfileStatusFields record={record} />
      {isSubmitting ? (
        <div className="yowan-stakeholder-profile-pending">
          <LoadingSpinner label={content.form.pendingMessage || '保存しています'} showLabel />
        </div>
      ) : null}
      {submitMessage ? <p className="yowan-admin-form__message">{submitMessage}</p> : null}
      <div className="yowan-admin-form__actions">
        <button className="c-button c-button--primary" type="submit" disabled={isSubmitting}>
          {isSubmitting ? '保存中' : '保存する'}
        </button>
      </div>
    </form>
  );
};

const OrganizerProfileForm = ({ record, onUpdated, onSaved, contentKey = '/stakeholder/profile' }) => {
  const content = yowanPageContent[contentKey] || yowanPageContent['/stakeholder/profile'];
  const [submitMessage, setSubmitMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({ defaultValues: { ...record, image: '' } });

  useEffect(() => {
    reset({ ...record, image: '' });
  }, [record, reset]);

  const onSubmit = async (values) => {
    setIsSubmitting(true);
    setSubmitMessage(content.form.pendingMessage);

    try {
      let updated = await updateYowanRelatedOrganizerProfile(record.id, values);
      const imageFile = getUploadFile(values.image);

      if (imageFile) {
        const imageUpdate = await updateYowanRelatedOrganizerProfileImage(record.id, imageFile);
        updated = imageUpdate?.id ? imageUpdate : { ...updated, ...imageUpdate };
      }

      onUpdated(updated);
      reset({ ...updated, image: '' });
      setSubmitMessage(content.form.updatedMessage);
      onSaved?.();
    } catch (error) {
      setSubmitMessage(getYowanAdminMutationErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form className="yowan-admin-form c-surface-content" onSubmit={handleSubmit(onSubmit)}>
      <header className="yowan-stakeholder-profile-card__header">
        <h2>主催者プロフィール</h2>
        <p>公開プロフィールとして表示される主催者情報を更新できます。</p>
      </header>
      <div className="c-form__field">
        <span className="c-form__label">主催者名<em className="c-form__required">*</em></span>
        <input className="c-form__control" type="text" {...register('organizerName', { required: 'この項目は必須です。' })} />
        {errors.organizerName ? <em className="yowan-admin-form__error">{errors.organizerName.message}</em> : null}
      </div>
      <div className="c-form__field">
        <span className="c-form__label">種別<em className="c-form__required">*</em></span>
        <select className="c-form__control" {...register('organizerType', { required: 'この項目は必須です。' })}>
          <option value="individual">個人</option>
          <option value="group">団体</option>
        </select>
        {errors.organizerType ? <em className="yowan-admin-form__error">{errors.organizerType.message}</em> : null}
      </div>
      <div className="c-form__field">
        <span className="c-form__label">代表者名</span>
        <input className="c-form__control" type="text" {...register('representativeName')} />
      </div>
      <div className="c-form__field">
        <span className="c-form__label">連絡先メール<em className="c-form__required">*</em></span>
        <input className="c-form__control" type="email" {...register('contactEmail', { required: 'この項目は必須です。' })} />
        {errors.contactEmail ? <em className="yowan-admin-form__error">{errors.contactEmail.message}</em> : null}
      </div>
      <div className="c-form__field">
        <span className="c-form__label">連絡先電話</span>
        <input className="c-form__control" type="text" {...register('contactPhone')} />
      </div>
      <div className="c-form__field">
        <span className="c-form__label">Webサイト</span>
        <input className="c-form__control" type="url" {...register('websiteUrl')} />
      </div>
      <div className="c-form__field">
        <span className="c-form__label">プロフィール画像</span>
        <AdminCurrentImagePreview image={record.primary_image} />
        <input className="c-form__control" type="file" accept="image/*" {...register('image', { validate: validateYowanImageUploadFile })} />
        {errors.image ? <em className="yowan-admin-form__error">{errors.image.message}</em> : null}
        <FieldNote>{YOWAN_IMAGE_UPLOAD_HELP_TEXT}</FieldNote>
      </div>
      <div className="c-form__field">
        <span className="c-form__label">紹介文</span>
        <textarea className="c-form__control" rows="5" maxLength="800" {...register('description')} />
        <FieldNote>800文字以内で入力してください。</FieldNote>
      </div>
      <ProfileStatusFields record={record} />
      {isSubmitting ? (
        <div className="yowan-stakeholder-profile-pending">
          <LoadingSpinner label={content.form.pendingMessage || '保存しています'} showLabel />
        </div>
      ) : null}
      {submitMessage ? <p className="yowan-admin-form__message">{submitMessage}</p> : null}
      <div className="yowan-admin-form__actions">
        <button className="c-button c-button--primary" type="submit" disabled={isSubmitting}>
          {isSubmitting ? '保存中' : '保存する'}
        </button>
      </div>
    </form>
  );
};

const StakeholderProfilePage = ({ guardOptions }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { pathname } = location;
  const contentKey = getStakeholderProfileRouteKey(pathname);
  const content = yowanPageContent[contentKey] || yowanPageContent['/stakeholder/profile'];
  const guard = useStakeholderGuard(guardOptions);
  const [profile, setProfile] = useState({ instructors: [], organizers: [] });
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    if (guard.status !== 'ready') {
      return undefined;
    }

    let isMounted = true;
    setIsLoading(true);
    setLoadError('');

    fetchYowanRelatedProfile()
      .then((payload) => {
        if (isMounted) {
          setProfile(payload);
        }
      })
      .catch(() => {
        if (isMounted) {
          setProfile({ instructors: [], organizers: [] });
          setLoadError(content?.loadErrorMessage || 'プロフィールを読み込めませんでした。');
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [content?.loadErrorMessage, guard.status]);

  const updateInstructor = (updated) => {
    setProfile((current) => ({
      ...current,
      instructors: current.instructors.map((item) => (item.id === updated.id ? updated : item)),
    }));
  };

  const updateOrganizer = (updated) => {
    setProfile((current) => ({
      ...current,
      organizers: current.organizers.map((item) => (item.id === updated.id ? updated : item)),
    }));
  };

  const redirectToStakeholderDashboard = () => {
    navigate(appendYowanPreviewQueryIfNeeded('/stakeholder', location.search));
  };

  if (guard.status !== 'ready') {
    return <StakeholderGuardNotice notice={guard.notice} />;
  }

  const showInstructorProfiles = contentKey !== '/stakeholder/organizer-profile';
  const showOrganizerProfiles = contentKey !== '/stakeholder/instructor-profile';
  const visibleInstructorCount = showInstructorProfiles ? profile.instructors.length : 0;
  const visibleOrganizerCount = showOrganizerProfiles ? profile.organizers.length : 0;
  const emptyMessage = content.emptyMessage || '更新できるプロフィールはありません。';

  return (
    <StakeholderShell activeNavHref={contentKey} routeKey={contentKey} currentUser={guard.currentUser}>
      <AdminPageHeader title={content.header.title} paragraphs={content.header.paragraphs} />
      {isLoading ? <div className="c-surface-content"><LoadingSpinner label="プロフィールを読み込み中" showLabel /></div> : null}
      {!isLoading && loadError ? <div className="c-surface-content"><p className="yowan-admin-form__message">{loadError}</p></div> : null}
      {!isLoading && !loadError && visibleInstructorCount === 0 && visibleOrganizerCount === 0 ? (
        <div className="c-surface-content"><p className="yowan-admin-form__message">{emptyMessage}</p></div>
      ) : null}
      {!isLoading && !loadError ? (
        <div className="yowan-stakeholder-profile-list">
          {showInstructorProfiles ? profile.instructors.map((record) => (
            <InstructorProfileForm
              key={record.id}
              record={record}
              onUpdated={updateInstructor}
              onSaved={redirectToStakeholderDashboard}
              contentKey={contentKey}
            />
          )) : null}
          {showOrganizerProfiles ? profile.organizers.map((record) => (
            <OrganizerProfileForm
              key={record.id}
              record={record}
              onUpdated={updateOrganizer}
              onSaved={redirectToStakeholderDashboard}
              contentKey={contentKey}
            />
          )) : null}
        </div>
      ) : null}
    </StakeholderShell>
  );
};

export default StakeholderProfilePage;
